/* Shared commercial-summary block — single source of truth for the
   "Gross → Less Special Offer → Taxable → GST → Total" footer used
   across Proposal Detail, PO Detail, Invoice Detail, and PDF/print
   views.

   Operator directive 2026-02 (Iter 67): every commercial artefact
   must render the same five rows when an offer is applied, so the
   discount is visible at every step of the document chain rather
   than buried in a separate informational block.

   Caller passes:
     - subtotal   — gross line-items total (Standard Commercial Value)
     - gst        — GST on the *post-discount* taxable base
     - total      — taxable + gst
     - specialOffer — proposal/PO/invoice's frozen offer snapshot
                      (.enabled, .label, .discount_amount,
                       .standard_value_at_apply, .net_value_at_apply)
     - currency   — for formatINR
     - dense      — when true, uses tighter padding (for compact
                    side-by-side panels like InvoiceDetail's totals box)
*/

import { formatINR } from "../../lib/format";

export function CommercialSummary({
    subtotal,
    gst,
    total,
    specialOffer,
    currency,
    dense = false,
    testIdPrefix = "commercial-summary",
}) {
    const hasOffer = !!(specialOffer && specialOffer.enabled);
    const padY = dense ? "py-1.5" : "py-3";
    const labelCls = `${padY} text-right reedos-eyebrow`;
    const dataCls = `${padY} text-right reedos-data text-sm`;

    if (!hasOffer) {
        return (
            <div className="space-y-1.5" data-testid={`${testIdPrefix}-no-offer`}>
                <Row label="Subtotal" value={formatINR(subtotal, currency)} testId={`${testIdPrefix}-subtotal`} labelCls={labelCls} dataCls={dataCls} />
                <Row label="GST" value={formatINR(gst, currency)} testId={`${testIdPrefix}-gst`} labelCls={labelCls} dataCls={dataCls} />
                <Row label="Total" value={formatINR(total, currency)} bold testId={`${testIdPrefix}-total`} labelCls={labelCls} dataCls={dataCls} />
            </div>
        );
    }

    // Gross subtotal — for currently-finalized version this equals the
    // offer's standard_value_at_apply, but for the live editable version
    // it may be the running sum (operator is mid-edit). Prefer the
    // caller's ``subtotal`` so the footer matches the table above.
    const grossSubtotal = subtotal;
    const discount = Number(specialOffer.discount_amount || 0);
    const taxable = Math.max(0, grossSubtotal - discount);

    return (
        <div className="space-y-1.5" data-testid={`${testIdPrefix}-with-offer`}>
            <Row
                label="Gross Commercial Value"
                value={formatINR(grossSubtotal, currency)}
                testId={`${testIdPrefix}-gross`}
                labelCls={labelCls}
                dataCls={dataCls}
            />
            <Row
                label={`Less: ${specialOffer.label || "Special Offer"}`}
                value={`− ${formatINR(discount, currency)}`}
                testId={`${testIdPrefix}-discount`}
                labelCls={`${labelCls} text-rose-600`}
                dataCls={`${dataCls} text-rose-600`}
            />
            <Row
                label="Taxable Value"
                value={formatINR(taxable, currency)}
                testId={`${testIdPrefix}-taxable`}
                labelCls={labelCls}
                dataCls={dataCls}
            />
            <Row
                label="GST"
                value={formatINR(gst, currency)}
                testId={`${testIdPrefix}-gst`}
                labelCls={labelCls}
                dataCls={dataCls}
            />
            <Row
                label="Total"
                value={formatINR(total, currency)}
                bold
                testId={`${testIdPrefix}-total`}
                labelCls={labelCls}
                dataCls={dataCls}
            />
        </div>
    );
}

function Row({ label, value, bold, testId, labelCls, dataCls }) {
    const boldExtra = bold ? "font-display font-bold text-base text-slate-900" : "";
    return (
        <div className={`flex items-baseline justify-between ${bold ? "pt-2 border-t border-slate-200" : ""}`} data-testid={testId}>
            <span className={labelCls}>{label}</span>
            <span className={`${dataCls} ${boldExtra}`}>{value}</span>
        </div>
    );
}

/* Table-row variant — for surfaces that need to render the 5 rows as
   <tr> inside an existing <tfoot> (Proposal Detail + PO Detail line
   items table). ``colSpan`` is how many empty cells go between the
   label cell and the value cell so the row spans the same width as
   the table above. */
export function CommercialSummaryRows({
    subtotal,
    gst,
    total,
    specialOffer,
    currency,
    colSpan,
    testIdPrefix = "commercial-summary",
}) {
    const hasOffer = !!(specialOffer && specialOffer.enabled);
    if (!hasOffer) {
        return (
            <>
                <tr data-testid={`${testIdPrefix}-subtotal-row`}>
                    <td className="px-4 py-3 reedos-eyebrow text-right">Subtotal</td>
                    <td colSpan={colSpan}></td>
                    <td className="px-4 py-3 text-right reedos-data text-sm" data-testid={`${testIdPrefix}-subtotal`}>{formatINR(subtotal, currency)}</td>
                </tr>
                <tr data-testid={`${testIdPrefix}-gst-row`}>
                    <td className="px-4 py-2 reedos-eyebrow text-right">GST</td>
                    <td colSpan={colSpan}></td>
                    <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`${testIdPrefix}-gst`}>{formatINR(gst, currency)}</td>
                </tr>
                <tr data-testid={`${testIdPrefix}-total-row`}>
                    <td className="px-4 py-3 text-right font-display font-bold text-slate-900">Total</td>
                    <td colSpan={colSpan}></td>
                    <td className="px-4 py-3 text-right reedos-data font-display font-bold text-base text-slate-900" data-testid={`${testIdPrefix}-total`}>{formatINR(total, currency)}</td>
                </tr>
            </>
        );
    }
    const grossSubtotal = subtotal;
    const discount = Number(specialOffer.discount_amount || 0);
    const taxable = Math.max(0, grossSubtotal - discount);
    return (
        <>
            <tr data-testid={`${testIdPrefix}-gross-row`}>
                <td className="px-4 py-3 reedos-eyebrow text-right">Gross Commercial Value</td>
                <td colSpan={colSpan}></td>
                <td className="px-4 py-3 text-right reedos-data text-sm" data-testid={`${testIdPrefix}-gross`}>{formatINR(grossSubtotal, currency)}</td>
            </tr>
            <tr data-testid={`${testIdPrefix}-discount-row`}>
                <td className="px-4 py-2 reedos-eyebrow text-right text-rose-600">Less: {specialOffer.label || "Special Offer"}</td>
                <td colSpan={colSpan}></td>
                <td className="px-4 py-2 text-right reedos-data text-sm text-rose-600" data-testid={`${testIdPrefix}-discount`}>− {formatINR(discount, currency)}</td>
            </tr>
            <tr data-testid={`${testIdPrefix}-taxable-row`}>
                <td className="px-4 py-2 reedos-eyebrow text-right">Taxable Value</td>
                <td colSpan={colSpan}></td>
                <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`${testIdPrefix}-taxable`}>{formatINR(taxable, currency)}</td>
            </tr>
            <tr data-testid={`${testIdPrefix}-gst-row`}>
                <td className="px-4 py-2 reedos-eyebrow text-right">GST</td>
                <td colSpan={colSpan}></td>
                <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`${testIdPrefix}-gst`}>{formatINR(gst, currency)}</td>
            </tr>
            <tr data-testid={`${testIdPrefix}-total-row`}>
                <td className="px-4 py-3 text-right font-display font-bold text-slate-900">Total</td>
                <td colSpan={colSpan}></td>
                <td className="px-4 py-3 text-right reedos-data font-display font-bold text-base text-slate-900" data-testid={`${testIdPrefix}-total`}>{formatINR(total, currency)}</td>
            </tr>
        </>
    );
}
